import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  completeGithubRedirectLogin,
  consumeAuthReturnTo,
  createAuthSession,
  defaultSignedInPath,
  resetGithubRedirectLogin,
  type MembershipResolution,
} from "@/modules/auth/public";
import { isHttpClientError, toErrorLogContext } from "@/shared/http";
import { createLogger } from "@/shared/logging";
import { useAppState } from "../state/AppStateProvider";
import { AuthCard, type AuthCardStatus } from "./AuthCard";
import { LoginButton } from "./LoginButton";

const logger = createLogger("app/authCallback");

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { startSession } = useAppState();
  const hasHandledResult = useRef(false);
  const [status, setStatus] = useState<AuthCardStatus>({
    kind: "pending",
    message: "GitHub 로그인 응답을 확인하는 중입니다.",
  });

  useEffect(() => {
    let disposed = false;

    void (async () => {
      setStatus({ kind: "pending", message: "Spawning Pool 세션을 만드는 중입니다." });
      // 이 실행이 결과를 맡았는지. 두 실행이 겹쳐도(StrictMode 재실행) 세션 시작은 한 번이다.
      let claimed = false;

      try {
        const loginResp = await completeGithubRedirectLogin();
        if (disposed || hasHandledResult.current) {
          return;
        }
        hasHandledResult.current = true;
        claimed = true;

        // Overlord 인증이 끝나도 Intake 가 Member 로 받아 줘야 세션이다 — 아니면 세션 없이 안내만 한다.
        const resolution = await startSession(createAuthSession(loginResp));
        if (disposed) {
          return;
        }
        if (resolution.kind !== "member") {
          logger.warn(
            "Intake did not accept the new session.",
            getMembershipLogContext(resolution),
          );
          setStatus({ kind: "failed", message: getMembershipFailureMessage(resolution) });
          return;
        }

        // 로그인 전에 가려던 곳이 있으면 그리로 돌려보낸다(딥링크·북마크 보존). 없으면 기본 착지다.
        navigate(consumeAuthReturnTo() ?? defaultSignedInPath, { replace: true });
      } catch (error) {
        if (disposed || (hasHandledResult.current && !claimed)) {
          return;
        }

        hasHandledResult.current = true;
        logger.warn("Failed to process the OAuth callback.", toErrorLogContext(error));
        setStatus({ kind: "failed", message: getAuthCallbackErrorMessage(error) });
      }
    })();

    return () => {
      disposed = true;
    };
  }, [navigate, startSession]);

  return (
    <AuthCard
      titleId="auth-callback-title"
      title={status.kind === "failed" ? "로그인하지 못했습니다." : "로그인을 처리하고 있습니다."}
      status={status}
    >
      {status.kind === "failed" ? (
        <LoginButton
          onClick={() => {
            resetGithubRedirectLogin();
            navigate("/", { replace: true });
          }}
        >
          로그인으로 돌아가기
        </LoginButton>
      ) : null}
    </AuthCard>
  );
}

// Overlord 오류 코드(hatchery_overlord.exceptions.codes)를 사용자 문구로 옮긴다.
function getAuthCallbackErrorMessage(error: unknown): string {
  if (isHttpClientError(error)) {
    switch (error.code) {
      case "auth.github.state.invalid":
      case "auth.github.token.invalid":
        return "GitHub 로그인 시간이 만료됐거나 요청을 확인하지 못했습니다. 다시 로그인해 주세요.";
      case "auth.github.org.forbidden":
        return "허용된 GitHub 조직 멤버만 Spawning Pool에 접근할 수 있습니다.";
      case "auth.login.unsupported_type":
        return "GitHub 로그인 요청 설정이 올바르지 않습니다.";
      case "auth.rate_limit.exceeded":
        return "로그인 요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.";
    }

    if (error.status === 401) {
      return "GitHub 인증은 완료됐지만 Spawning Pool 세션을 만들지 못했습니다.";
    }

    if (error.status === 403) {
      return "허용된 GitHub 조직 멤버만 Spawning Pool에 접근할 수 있습니다.";
    }

    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "OAuth 로그인을 완료하지 못했습니다.";
}

type MembershipFailure = Exclude<MembershipResolution, { kind: "member" }>;

// Intake 가 세션을 받아 주지 않은 이유를 사용자 문구로 옮긴다(오류 코드는 spnp 계약, docs/api.md).
function getMembershipFailureMessage(failure: MembershipFailure): string {
  if (failure.kind === "unavailable") {
    return "Spawning Pool 서버에 연결하지 못해 로그인을 마치지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }

  switch (failure.code) {
    case "member.not_found":
      return "이 GitHub 계정은 Spawning Pool 조직의 Member 로 등록되어 있지 않습니다. 관리자에게 등록을 요청해 주세요.";
    case "member.inactive":
      return "이 계정의 Spawning Pool 접근이 회수되었습니다. 다시 사용하려면 관리자에게 요청해 주세요.";
    default:
      return "GitHub 인증은 완료됐지만 Spawning Pool 세션을 확인하지 못했습니다. 다시 로그인해 주세요.";
  }
}

function getMembershipLogContext(failure: MembershipFailure): unknown {
  return failure.kind === "rejected"
    ? failure.error.toLogContext()
    : toErrorLogContext(failure.error);
}
